<hlm-dialog-surface size="md">
  <hlm-dialog-header>
    <hlm-dialog-icon-badge [tone]="phase() === 'result' ? 'success' : 'primary'">
      @if (phase() === 'result') {
        <hlm-icon name="lucideCheck" size="1.1rem" />
      } @else {
        <hlm-icon name="lucideKeyRound" size="1.1rem" />
      }
    </hlm-dialog-icon-badge>
    <div hlmDialogHeading>
      @if (phase() === 'result') {
        <h2 hlmDialogTitle>TAN generated</h2>
        <p hlmDialogDescription>
          Read this number to the caller. Expires {{ expiresRelative() }}.
        </p>
      } @else {
        <h2 hlmDialogTitle>Generate TAN</h2>
        <p hlmDialogDescription>
          Customer {{ customerName() }} · UA {{ username() }}
        </p>
      }
    </div>
    <button hlmDialogClose tabindex="-1" (click)="cancel()">
      <hlm-icon name="lucideX" size="1rem" />
    </button>
  </hlm-dialog-header>

  <section hlmDialogBody>
    @if (phase() === 'form' || phase() === 'submitting' || phase() === 'error') {
      <div class="flex flex-col gap-4">

        <!-- Create TAN for employee -->
        <div class="flex flex-col gap-1.5">
          <label class="cmt-eyebrow">
            Create TAN for employee
            <span class="text-destructive" aria-hidden="true">*</span>
          </label>
          <cmt-id-autocomplete
            [customSearchFn]="employeeSearchFn"
            [resolvedLabel]="selectedLabel()"
            [disabledInput]="phase() === 'submitting'"
            [minChars]="3"
            placeholder="Search by name, login, ID…"
            (hitSelected)="onEmployeeHit($event)"
          />
          <p class="text-sm text-muted-foreground">
            The TAN will only be accepted when this employee authenticates.
          </p>
        </div>

        <!-- Validity -->
        <div class="flex flex-col gap-1.5">
          <label class="cmt-eyebrow">
            Validity
          </label>
          <select
            hlmNativeSelect
            [disabled]="phase() === 'submitting'"
            [ngModel]="validityHours()"
            (ngModelChange)="onValidityChange($any($event))"
          >
            @for (h of validityOptions; track h) {
              <option [value]="h">{{ h }} {{ h === 1 ? 'hour' : 'hours' }}</option>
            }
          </select>
        </div>

        @if (phase() === 'error' && errorMsg()) {
          <div class="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 shrink-0" />
            <span>{{ errorMsg() }}</span>
          </div>
        }
      </div>
    }

    @if (phase() === 'result' && generated(); as tan) {
      <div class="flex flex-col items-center gap-5 py-2">
        <div class="flex items-center gap-3 rounded-2xl border border-border bg-muted/40 px-6 py-5">
          <span class="font-mono-feat text-5xl font-semibold tracking-[0.2em] text-foreground">
            {{ tan.tan }}
          </span>
          <button
            hlmBtn
            variant="ghost"
            size="icon"
            cdkOverlayOrigin
            #shareOrigin="cdkOverlayOrigin"
            type="button"
            (click)="shareOpen.set(!shareOpen())"
            [attr.aria-label]="justCopied() ? 'Copied' : 'Share TAN'"
            aria-haspopup="menu"
            [attr.aria-expanded]="shareOpen()"
          >
            @if (justCopied()) {
              <hlm-icon name="lucideCheck" size="1rem" class="text-primary" />
            } @else {
              <hlm-icon name="lucideShare2" size="1rem" />
            }
          </button>
          <ng-template
            cdkConnectedOverlay
            [cdkConnectedOverlayOrigin]="shareOrigin"
            [cdkConnectedOverlayOpen]="shareOpen()"
            [cdkConnectedOverlayHasBackdrop]="true"
            cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
            [cdkConnectedOverlayPositions]="[
              { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
              { originX: 'end', originY: 'top',    overlayX: 'end', overlayY: 'bottom', offsetY: -6 }
            ]"
            (backdropClick)="shareOpen.set(false)"
            (detach)="shareOpen.set(false)"
          >
            <div
              class="z-50 min-w-[14rem] overflow-hidden rounded-lg border border-border bg-popover p-1 shadow-[0_2px_4px_rgba(20,18,30,0.04),0_24px_48px_-12px_rgba(20,18,30,0.18)]"
              role="menu"
              aria-label="Share TAN"
            >
              <div class="px-3 pb-1.5 pt-2.5 font-mono-feat text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Share TAN
              </div>
              <div class="flex flex-col gap-0.5 p-1.5">
                <button
                  type="button"
                  role="menuitem"
                  class="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-primary/10 hover:text-foreground focus:bg-primary/15 focus:outline-none"
                  (click)="shareOpen.set(false); copy()"
                >
                  <hlm-icon name="lucideCopy" size="1rem" class="text-muted-foreground" />
                  <span>Copy code</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-primary/10 hover:text-foreground focus:bg-primary/15 focus:outline-none"
                  (click)="shareOpen.set(false); onShareEmail()"
                >
                  <hlm-icon name="lucideMail" size="1rem" class="text-muted-foreground" />
                  <span>Email…</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  class="flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors hover:bg-primary/10 hover:text-foreground focus:bg-primary/15 focus:outline-none"
                  (click)="shareOpen.set(false); onShareTeams()"
                >
                  <hlm-icon name="lucideMessageSquare" size="1rem" class="text-muted-foreground" />
                  <span>Send via Teams…</span>
                </button>
              </div>
            </div>
          </ng-template>
        </div>

        <div class="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span>For: <span class="font-medium text-foreground">{{ targetLabel() }}</span></span>
          <span aria-hidden="true">·</span>
          <span>Expires {{ expiresRelative() }}</span>
        </div>
      </div>
    }
  </section>

  <footer hlmDialogFooter>
    @if (phase() === 'form' || phase() === 'error') {
      <button hlmBtn variant="outline" (click)="cancel()">Cancel</button>
      <button hlmBtn variant="default" (click)="submit()" [disabled]="!canSubmit()">
        <hlm-icon name="lucideKeyRound" size="0.9rem" />
        Generate
      </button>
    } @else if (phase() === 'submitting') {
      <button hlmBtn variant="outline" (click)="cancel()">Cancel</button>
      <button hlmBtn variant="default" [disabled]="true">
        <hlm-spinner size="16" />
        Generating...
      </button>
    } @else {
      <button hlmBtn variant="default" (click)="close()">Done</button>
    }
  </footer>
</hlm-dialog-surface>
